import { useCompany, usePermissions } from '@dispatch/shared/hooks';
import {
  AttributeDefinition,
  CertAndLicensesForm,
  CompanyAdminMeta,
  CompanyRole,
  Group,
  GroupAttributeDefinition,
  GroupAttributeMatchMode,
  MultiSelectOption,
  Office,
  Team,
} from '@dispatch/shared/types';
import {
  CheckBoxField,
  DrawerButton,
  Form,
  RenderFuncProps,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeleteOutline } from '@mui/icons-material';
import { Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useGeneralDataContext } from '@dispatch/shared/hooks';
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useIsMutating } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { CompanyAttributesForm } from './CompanyAttributesForm';

const checkBoxStyles = {
  label: { margin: 0 },
  checkbox: { padding: 0, margin: 0 },
};

const FormButtons = ({
  handleCancel,
  handleSubmit,
}: {
  handleCancel: () => void;
  handleSubmit: () => Promise<void>;
}) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const mutating = useIsMutating(['upsert-company']);
  const isUpserting = mutating > 0;
  return (
    <Stack direction="row" justifyContent="end" paddingTop={1} gap={1}>
      <DrawerButton
        dataTestId="company-admin-cancel-button"
        text={'Cancel'}
        onClick={handleCancel}
        isDisabled={false}
        color={'error'}
        variant={'outlined'}
      />

      <DrawerButton
        dataTestId="company-admin-save-button"
        text={isUpserting ? 'Saving' : 'Save'}
        onClick={handleSubmit}
        isDisabled={!hasPermissions || isUpserting}
        color={'info'}
        backgroundColor={'#2196F3'}
        variant={'contained'}
      />
    </Stack>
  );
};

type CompanyAdminModalProps = {
  handleSave: (
    body: unknown & { integrationGUID: string },
    key?: string
  ) => void;
  handleCancel: () => void;
  id?: number;
  modalState?: CompanyAdminMeta;
  groupAttributes?: CompanyGroupAttributes[];
};

const attributesResolver = yup.object().shape({
  displayName: yup.string().required('Name is required'),
  attributeDefinitionValues: yup
    .array()
    .of(
      yup.object().shape({
        displayName: yup.string().required('Attribute is required'),
        value: yup.string().required('Value is required'),
      })
    )
    .min(1, 'At least one attribute value is required'),
});

export const AddAttributesContent = ({
  handleSave,
  handleCancel,
  id,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const editData = modalState?.addAttribute?.data;
  const newAttribute = {
    id,
    integrationGUID: uuid,
    displayName: '',
    isActive: true,
    externalAttributeDefinitionId: '',
    isDispatchPerilDefault: false,
    isDispatchLanguageDefault: false,
    attributeDataType: 'Single-select list (string)',
    attributeDefinitionValues: [
      {
        id: 0,
        isActive: true,
        integrationGUID: uuidv4(),
        attributeDefinitionId: 0,
        externalAttributeDefinitionValueId: '',
        displayName: '',
        value: '',
      },
    ],
  };
  const appendAttributeDefinition = () => {
    const newAttributeId = uuidv4();
    const newAttribute = {
      id: 0,
      isActive: true,
      integrationGUID: newAttributeId,
      attributeDefinitionId: 0,
      externalAttributeDefinitionValueId: '',
      displayName: '',
      value: '',
    };
    return append(newAttribute);
  };
  const values = editData ? editData : newAttribute;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(attributesResolver),
    defaultValues: { ...values },
  });
  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name: 'attributeDefinitionValues',
  });

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                disabled={!hasPermissions}
                id="displayName"
                name="displayName"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Stack>
              <CheckBoxField
                sx={checkBoxStyles}
                id="isDispatchPerilDefault"
                name="isDispatchPerilDefault"
                label="Dispatch Peril Default"
                control={control}
              />
              <CheckBoxField
                sx={checkBoxStyles}
                id="isDispatchLanguageDefault"
                name="isDispatchLanguageDefault"
                label="Dispatch Language Default"
                control={control}
              />
            </Stack>
            <Grid size={12}>
              <TextField
                id={`externalAttributeDefinitionValueId`}
                name={`externalAttributeDefinitionValueId`}
                label="External Attribute DefinitionValue Id"
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid size={12}>
              <SelectField
                id={`attributeDataType`}
                name={`attributeDataType`}
                label="Attribute Data Type"
                required
                control={control}
                errors={errors}
                options={[
                  {
                    title: 'Single-select list (string)',
                    value: 'Single-select list (string)',
                  },
                ]}
              />
            </Grid>
            <Grid padding="0 30px 30px 0" size={12}>
              <Section title="Attribute Values">
                <Grid sx={{ overflow: 'scroll', height: '300px' }} size={12}>
                  {fields.map(
                    (item: { id: string; attribute?: string }, index) => {
                      return (
                        <>
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            key={item.id}
                          >
                            <Grid size={12}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                              >
                                <TextField
                                  id={`attributeDefinitionValues.${index}.displayName`}
                                  name={`attributeDefinitionValues.${index}.displayName`}
                                  label="Attribute"
                                  required
                                  control={control}
                                  errors={errors}
                                  sx={{ width: '100%' }}
                                  error={
                                    (Array.isArray(
                                      errors.attributeDefinitionValues
                                    ) &&
                                      errors.attributeDefinitionValues?.[
                                        index
                                      ] &&
                                      errors.attributeDefinitionValues?.[index]
                                        .displayName?.message) ||
                                    ''
                                  }
                                />
                                {fields.length > 1 && (
                                  <Button
                                    variant="text"
                                    color="error"
                                    onClick={() => remove(index)}
                                  >
                                    <DeleteOutline />
                                  </Button>
                                )}
                              </Stack>
                            </Grid>
                            <Grid size={12}>
                              <TextField
                                id={`attributeDefinitionValues.${index}.value`}
                                name={`attributeDefinitionValues.${index}.value`}
                                label="Value"
                                required
                                control={control}
                                error={
                                  (Array.isArray(
                                    errors.attributeDefinitionValues
                                  ) &&
                                    errors.attributeDefinitionValues?.[index] &&
                                    errors.attributeDefinitionValues?.[index]
                                      .value?.message) ||
                                  ''
                                }
                                sx={{ width: '100%' }}
                              />
                            </Grid>
                          </Grid>
                          <Divider sx={{ margin: '20px 0' }} />
                        </>
                      );
                    }
                  )}
                </Grid>
              </Section>
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<AttributeDefinition>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginTop="10px"
      >
        <Button onClick={() => appendAttributeDefinition()} variant="text">
          Add Attribute Values
        </Button>
        <FormButtons
          handleCancel={handleCancel}
          handleSubmit={() =>
            handleSubmit((values) => {
              const attribute = values;
              // add id and attributeDefinitionId to each attributeDefinitionValues. They should start at 0, and increment by 1
              attribute.attributeDefinitionValues =
                values.attributeDefinitionValues.map((item, index) => {
                  item.id = index;
                  item.attributeDefinitionId = index;
                  return item;
                });

              return handleSave(attribute, 'attributeDefinition');
            })()
          }
        />
      </Stack>
    </Stack>
  );
};

const attributeSchema = yup.object().shape({
  attributeDefinitionIntegrationGUID: yup.string().required(),
  displayName: yup.string().required(),
  selectedAttributeValuesVarchars: yup
    .array()
    .of(
      yup.object().shape({
        attributeDefinitionValueIntegrationGUID: yup.string().required(),
      })
    )
    .min(1, 'Each attribute must have at least one value selected')
    .required('Each attribute must have at least one value selected'),
});

// Helper function to create a sorted signature of selected attribute values for comparison
const createAttributeSignature = (attributes: any[]): string => {
  const allSelectedValues: string[] = [];

  attributes?.forEach((attr) => {
    attr.selectedAttributeValuesVarchars?.forEach((value: any) => {
      // Use the actual value title/displayName for comparison
      const valueSignature =
        value.title || value.displayName || value.value || '';
      if (valueSignature) {
        allSelectedValues.push(valueSignature.toLowerCase().trim());
      }
    });
  });

  return allSelectedValues.sort().join('|');
};

// Helper function to create attribute signature from CompanyGroupAttributes format
const createAttributeSignatureFromGroupAttribute = (
  groupAttribute: CompanyGroupAttributes
): string => {
  const allSelectedValues: string[] = [];

  groupAttribute.selectedAttributeValuesVarchars?.forEach((value) => {
    const valueSignature =
      value.title || value.displayName || value.value || '';
    if (valueSignature) {
      allSelectedValues.push(valueSignature.toLowerCase().trim());
    }
  });

  return allSelectedValues.sort().join('|');
};

// Function to create resolver with existing group names validation
const groupAttributesResolver = (
  existingGroups: CompanyGroupAttributes[] = [],
  editingGroupId?: string
) =>
  yup.object().shape({
    displayName: yup
      .string()
      .required('Name is required')
      .test(
        'unique-name',
        'A group with this name already exists',
        function (displayName) {
          if (!displayName) return true; // Let required validation handle empty values

          // Check if another group (not the one being edited) has the same name
          const isDuplicate = existingGroups.some(
            (group) =>
              group.displayName?.toLowerCase().trim() ===
                displayName.toLowerCase().trim() &&
              group.integrationGUID !== editingGroupId
          );

          return !isDuplicate;
        }
      ),
    attributes: yup
      .array()
      .of(attributeSchema)
      .min(2, 'At least 2 different attribute types must be selected')
      .required('At least 2 different attribute types must be selected')
      .test(
        'unique-attribute-combination',
        'A group with this exact combination of attributes already exists',
        function (attributes) {
          //.test runs regardless of validation being present to not allow an empty array so still need to check for it here to make sure we bypass the test incase of emoty array
          if (!attributes || attributes.length === 0) return true;
          const currentSignature = createAttributeSignature(attributes);

          const isDuplicateAttributes = existingGroups.some((group) => {
            if (group.integrationGUID === editingGroupId) {
              return false; // Skip group which is being edited
            }

            const existingSignature =
              createAttributeSignatureFromGroupAttribute(group);
            return (
              existingSignature === currentSignature && currentSignature !== ''
            ); // Don't compare empty signatures
          });

          return !isDuplicateAttributes;
        }
      ),
    addSelectedAttributeGUID: yup.mixed().nullable().optional(), // Allow mixed type or null
  });

export interface CompanyGroupAttributeValuesVarcharsForm {
  integrationGUID?: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  value: string | undefined;
  displayName?: string;
}

export type CompanyGroupAttributes = {
  integrationGUID: string;
  displayName?: string;
  attributeDefinitionIntegrationGUID: string;
  selectedAttributeValuesVarchars: CompanyGroupAttributeValuesVarcharsForm[];
  matchMode?: GroupAttributeMatchMode;
};

export type GroupAttributeFormData = {
  integrationGUID: string;
  displayName: string;
  attributes?: CompanyGroupAttributes[]; //this is attributes definition inside each group attribute
  addSelectedAttributeGUID: MultiSelectOption[];
};

export const AddGroupAttributeContent = ({
  handleSave,
  handleCancel,
  id,
  modalState,
  groupAttributes,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const editData = modalState?.addGroupAttribute?.data;
  const { company } = useCompany();

  const newGroupAttribute: GroupAttributeFormData = {
    integrationGUID: uuidv4(),
    displayName: '',
    attributes: [],
    addSelectedAttributeGUID: [],
  };

  // Transform edit data to form format
  const transformEditData = (data: any): GroupAttributeFormData => {
    if (!data) return newGroupAttribute;

    // Group the selectedAttributeValuesVarchars by selectedAttributeDefinitionIntegrationGUID
    // Each group represents one attribute with its selected values
    const groupedAttributes = new Map();

    data.selectedAttributeValuesVarchars?.forEach((item: any) => {
      const key = item.selectedAttributeDefinitionIntegrationGUID;
      if (!groupedAttributes.has(key)) {
        // Find the attribute definition to get the proper attribute name
        const attributeDef = company?.attributeDefinition?.find(
          (attr) => attr.integrationGUID === key
        );

        groupedAttributes.set(key, {
          integrationGUID: uuidv4(),
          attributeDefinitionIntegrationGUID: key,
          displayName: attributeDef?.displayName || 'Unknown Attribute', // Use attribute definition name, not value name
          selectedAttributeValuesVarchars: [],
          matchMode: data.valueMatchModes?.[key] ?? GroupAttributeMatchMode.ANY,
        });
      }

      // Add this value to the attribute's selected values
      groupedAttributes.get(key).selectedAttributeValuesVarchars.push({
        selectedAttributeDefinitionIntegrationGUID: key,
        attributeDefinitionValueIntegrationGUID:
          item.attributeDefinitionValueIntegrationGUID,
        title: item.title, // This is the actual selected value name like "Fire", "Water"
        value: item.value,
        displayName: item.title,
      });
    });

    const transformedData = {
      integrationGUID: data.integrationGUID || uuidv4(),
      displayName: data.displayName || '',
      attributes: Array.from(groupedAttributes.values()),
      addSelectedAttributeGUID: [],
    };

    return transformedData;
  };

  const values = editData ? transformEditData(editData) : newGroupAttribute;

  // Create resolver with existing group attributes for duplicate name validation
  const dynamicResolver = React.useMemo(() => {
    const editingGroupId = editData?.integrationGUID;
    return groupAttributesResolver(groupAttributes || [], editingGroupId);
  }, [groupAttributes, editData?.integrationGUID]);

  const form = useForm<GroupAttributeFormData>({
    resolver: yupResolver(dynamicResolver),
    defaultValues: { ...values },
  });

  const [originalValues] = React.useState<GroupAttributeFormData>(values);
  const [hasChanges, setHasChanges] = React.useState(!editData); // true for add mode, false for edit mode

  // Watch form values to detect changes in edit mode
  const watchedValues = form.watch();

  React.useEffect(() => {
    if (!editData) return; // Only handle edit mode

    // For edit mode, compare with original values
    const isChanged =
      watchedValues.displayName !== originalValues.displayName ||
      JSON.stringify(watchedValues.attributes) !==
        JSON.stringify(originalValues.attributes);

    setHasChanges(isChanged);
  }, [watchedValues, originalValues, editData]);

  const {
    fields: attributeFields,
    append: appendAttribute,
    remove: removeAttribute,
  } = useFieldArray({
    control: form.control,
    name: 'attributes',
  });

  const handleAddAttribute = (value: unknown) => {
    const rawValue = value as { title: string; value: string };
    const selectedAttribute = getSelectedAttributeData(rawValue.value);

    appendAttribute(selectedAttribute);

    // Clear the dropdown selection after adding
    setValue('addSelectedAttributeGUID', []);
  };

  type FormAttribute = {
    integrationGUID: string;
    attributeDefinitionIntegrationGUID: string;
    displayName: string;
    matchMode?: GroupAttributeMatchMode;
    selectedAttributeValuesVarchars: {
      selectedAttributeDefinitionIntegrationGUID: string;
      attributeDefinitionValueIntegrationGUID: string;
      title: string;
      value: string;
      displayName?: string;
    }[];
  };

  // Builds a blank form row for a newly selected attribute definition, defaulting matchMode to 'ANY'.
  const getSelectedAttributeData = (integrationGUID: string): FormAttribute => {
    const selectedAttribute = company?.attributeDefinition?.find(
      (attribute) => attribute.integrationGUID === integrationGUID
    );

    return {
      integrationGUID: uuidv4(),
      attributeDefinitionIntegrationGUID:
        selectedAttribute?.integrationGUID || '',
      displayName: selectedAttribute?.displayName || '',
      selectedAttributeValuesVarchars: [],
      matchMode: GroupAttributeMatchMode.ANY,
    };
  };

  const getFilteredOptions = () => {
    const formAttributes = form.watch('attributes');
    const companyAttributes = company?.attributeDefinition;

    if (!formAttributes || !companyAttributes) {
      return (
        companyAttributes?.map((value) => {
          return {
            title: value.displayName,
            value: value.integrationGUID,
          };
        }) || []
      );
    }

    const filteredCompanyAttributes = companyAttributes?.filter(
      (companyAttr) => {
        return !formAttributes.find(
          (formAttr) =>
            formAttr.attributeDefinitionIntegrationGUID ===
            companyAttr.integrationGUID
        );
      }
    );

    return filteredCompanyAttributes.map((value) => {
      return {
        title: value.displayName,
        value: value.integrationGUID,
      };
    });
  };

  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { errors: formErrors },
    control: formControl,
  } = form;

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <>
          <Section>
            <Grid container spacing={2} alignItems="center">
              <Grid size={4}>
                <TextField
                  disabled={!hasPermissions}
                  id="displayName"
                  name="displayName"
                  label="Attribute Group Name"
                  required
                  control={control}
                  errors={errors}
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Section>
          <Section title="Resource Group Attributes">
            <Grid container>
              <Grid size={6}>
                <SelectField
                  id="addSelectedAttributeGUID"
                  name="addSelectedAttributeGUID"
                  label="Add New Attribute"
                  control={control}
                  onValueChange={handleAddAttribute}
                  options={getFilteredOptions()}
                />
              </Grid>
            </Grid>

            {/* Display validation error for attributes only after submit attempt */}
            {errors.attributes?.message && form.formState.isSubmitted && (
              <Grid size={12}>
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {errors.attributes.message}
                </Typography>
              </Grid>
            )}

            <Divider
              sx={{ my: 1, borderColor: '#ccc', borderBottomWidth: 1 }}
            />
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {attributeFields.map((field, index) => (
                <CompanyAttributesForm
                  key={`${field.id}`}
                  control={control}
                  index={index}
                  removeAttribute={removeAttribute}
                  watch={form.watch}
                  field={field}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                />
              ))}
            </div>
            {/* needed or button will hide inputs */}
            <Grid container>
              <Grid
                sx={{ height: '100px' }}
                size={{
                  xs: 12,
                  sm: 6,
                }}
              ></Grid>
            </Grid>
          </Section>
        </>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<GroupAttributeDefinition>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <Stack direction="row" justifyContent="end" paddingTop={1} gap={1}>
        <DrawerButton
          dataTestId="company-admin-cancel-button"
          text={'Cancel'}
          onClick={handleCancel}
          isDisabled={false}
          color={'error'}
          variant={'outlined'}
        />
        <DrawerButton
          dataTestId="company-admin-save-button"
          text={editData ? 'Update' : 'Save'}
          isDisabled={!hasChanges}
          onClick={() =>
            form.handleSubmit((values) => {
              handleSave(values, 'groupAttribute');
            })()
          }
          color={'info'}
          backgroundColor={'#2196F3'}
          variant={'contained'}
        />
      </Stack>
    </Stack>
  );
};

const resourceResolver = yup.object().shape({
  name: yup.string().required('Name is required'),
});
export const AddResourceRoleContent = ({
  handleSave,
  handleCancel,
  id,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const editData = modalState?.addResourceRole?.data;
  const newRole = {
    id,
    integrationGUID: uuid,
    externalRoleId: uuid,
    name: '',
    attributeDefinitionToRoleAssignment: [],
    certificateAndLicenseToRoleAssignment: [],
  };
  const values = editData ? editData : newRole;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(resourceResolver),
    defaultValues: { ...values },
  });

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                id="name"
                name="name"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<CompanyRole>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <FormButtons
        handleCancel={handleCancel}
        handleSubmit={() =>
          handleSubmit((values) => handleSave(values, 'role'))()
        }
      />
    </Stack>
  );
};

const teamResolver = yup.object().shape({
  name: yup.string().required('Name is required'),
  externalTeamId: yup.string().required('External Team Id is required'),
});
export const AddTeamContent = ({
  handleSave,
  handleCancel,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const editData = modalState?.addTeam?.data;
  const newTeam = {
    integrationGUID: uuid,
    externalTeamId: uuid,
    name: '',
  };
  const values = editData ? editData : newTeam;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(teamResolver),
    defaultValues: { ...values },
  });

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                id={`externalTeamId`}
                name={`externalTeamId`}
                label="External Team Id"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
              <TextField
                id="name"
                name="name"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<Team>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <FormButtons
        handleCancel={handleCancel}
        handleSubmit={() =>
          handleSubmit((values) => handleSave(values, 'team'))()
        }
      />
    </Stack>
  );
};

const groupResolver = yup.object().shape({
  name: yup.string().required('Name is required'),
});
export const AddGroupContent = ({
  handleSave,
  handleCancel,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const editData = modalState?.addGroup?.data;
  const newGroup = {
    integrationGUID: uuid,
    externalGroupId: uuid,
    name: '',
  };
  const values = editData ? editData : newGroup;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(groupResolver),
    defaultValues: { ...values },
  });

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                id="name"
                name="name"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<Group>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <FormButtons
        handleCancel={handleCancel}
        handleSubmit={() =>
          handleSubmit((values) => {
            // if (!values.externalGroupId) {
            //   values.externalGroupId = uuidv4();
            // }

            handleSave(values, 'group');
          })()
        }
      />
    </Stack>
  );
};

const officeResolver = yup.object().shape({
  name: yup.string().required('Name is required'),
});
export const AddOfficeContent = ({
  handleSave,
  handleCancel,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const editData = modalState?.addOffice?.data;
  const newOffice = {
    integrationGUID: uuid,
    externalOfficeId: uuid,
    name: '',
  };
  const values = editData ? editData : newOffice;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(officeResolver),
    defaultValues: { ...values },
  });

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                id="name"
                name="name"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<Office>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <FormButtons
        handleCancel={handleCancel}
        handleSubmit={() =>
          handleSubmit((values) => handleSave(values, 'office'))()
        }
      />
    </Stack>
  );
};

const certsAndLicenseResolver = yup.object().shape({
  name: yup.string().required('Name is required'),
  organization: yup.string().required('Organization is required'),
  description: yup.string().required('Description is required'),
  isStateSpecific: yup.boolean(),
  stateAbbreviation: yup.string().when('isStateSpecific', {
    is: true,
    then: yup.string().required('Must select a state'),
  }),
});

export const AddLicenseAndCertContent = ({
  handleSave,
  handleCancel,
  id,
  modalState,
}: CompanyAdminModalProps) => {
  const { permissions } = usePermissions();
  const hasPermissions = !!permissions?.can('edit', 'company-page');
  const uuid = uuidv4();
  const { statesOptions } = useGeneralDataContext();
  const modalData = React.useMemo(
    () => modalState?.addCertAndLicense?.data,
    [modalState?.addCertAndLicense.data]
  );

  const editData = modalData;
  const newCertAndLicense = {
    id,
    integrationGUID: uuid,
    name: '',
    description: '',
    organization: '',
    requireExpirationDate: false,
    requireDocumentation: false,
    isRequired: false,
    isRequiredForStaff: false,
    verificationType: 'License',
    isActive: true,
    externalCertificationAndLicenseId: null,
    isStateSpecific: false,
    stateAbbreviation: '', // this is just for the form
    stateProvince: [],
  };

  const values = editData ? editData : newCertAndLicense;
  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(certsAndLicenseResolver),
    defaultValues: {
      ...values,
      stateProvinceId: values.stateProvince?.[0]?.id,
      stateProvince: values.stateProvince?.[0],
      stateAbbreviation: values.stateProvince?.[0]?.abbreviation,
      isRequired: values.stateProvince?.[0]?.isRequired,
      isRequiredForStaff: values.stateProvince?.[0]?.isRequiredForStaff,
    },
  });

  React.useEffect(() => {
    // if the user selects isStateSpecific to false, we need to clear the stateProvince if one is selected so the data is accurate
    if (watch('isStateSpecific') === false) {
      setValue('stateAbbreviation', '');
    }
  }, [watch('isStateSpecific')]);

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section>
          <Grid container spacing={2} alignItems="center">
            <Grid size={12}>
              <TextField
                id="name"
                name="name"
                label="Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                id="organization"
                name="organization"
                label="Organization"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                id="description"
                name="description"
                label="Description"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid size={12}>
              <SelectField
                id="verificationType"
                name="verificationType"
                label="Verification Type"
                control={control}
                options={[
                  { title: 'License', value: 'License' },
                  { title: 'Certification', value: 'Certification' },
                ]}
              />
            </Grid>
            <Grid size={12}>
              <CheckBoxField
                sx={checkBoxStyles}
                id="isStateSpecific"
                name="isStateSpecific"
                label="State Specific"
                control={control}
              />
              {watch('isStateSpecific') === true && (
                <SelectField
                  control={control}
                  id="stateAbbreviation"
                  name="stateAbbreviation"
                  label="State Province"
                  options={statesOptions}
                  errors={errors}
                />
              )}
            </Grid>
            <Grid size={12}>
              <Stack>
                <CheckBoxField
                  sx={checkBoxStyles}
                  id="isRequired"
                  name="isRequired"
                  label="Is Required"
                  control={control}
                />
                <CheckBoxField
                  sx={checkBoxStyles}
                  id="isRequiredForStaff"
                  name="isRequiredForStaff"
                  label="Is Required For Staff"
                  control={control}
                />
              </Stack>
            </Grid>
            <Stack>
              <CheckBoxField
                sx={checkBoxStyles}
                id="requireExpirationDate"
                name="requireExpirationDate"
                label="Require Expiration Date"
                control={control}
              />
              <CheckBoxField
                sx={checkBoxStyles}
                id="requireDocumentation"
                name="requireDocumentation"
                label="Require Documentation"
                control={control}
              />
              <CheckBoxField
                sx={checkBoxStyles}
                id="isActive"
                name="isActive"
                label="Active"
                control={control}
              />
            </Stack>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <Stack padding={2}>
      <Form<Partial<CertAndLicensesForm>>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
      <FormButtons
        handleCancel={handleCancel}
        handleSubmit={() =>
          handleSubmit((values) => {
            const valuesToSave = {
              // create the stateProvince object here
              ...values,
              stateProvince: [
                {
                  id:
                    values.stateProvinceId ||
                    Math.floor(Math.random() * 90000) + 10000, // we are unable to use a uuid here because the backend is expecting a long
                  abbreviation: values.stateAbbreviation,
                  state: statesOptions.find(
                    (state) => state.value === values.stateAbbreviation
                  )?.title,
                  isRequired: values.isRequired,
                  isRequiredForStaff: values.isRequiredForStaff,
                },
              ],
            };
            return handleSave(valuesToSave, 'certificationAndLicense');
          })()
        }
      />
    </Stack>
  );
};

export const DeleteContent = ({
  handleDelete,
  handleCancel,
  modalState,
}: Partial<CompanyAdminModalProps> & {
  handleDelete: (type: string, integrationGUID: string) => void;
}) => {
  return (
    <Stack padding={2}>
      <Typography>
        Are you sure you want to delete this item? This action cannot be undone.
      </Typography>
      <Stack direction="row" justifyContent="end" paddingTop={1} gap={1}>
        <DrawerButton
          dataTestId="company-admin-cancel-button"
          text={'Cancel'}
          onClick={handleCancel}
          isDisabled={false}
          color={'error'}
          variant={'outlined'}
        />

        <DrawerButton
          dataTestId="company-admin-save-button"
          text="Delete"
          isDisabled={false}
          onClick={() => {
            handleDelete(
              modalState?.delete.data?.type || '',
              modalState?.delete.data?.integrationGUID || ''
            );
          }}
          color={'info'}
          backgroundColor={'#2196F3'}
          variant={'contained'}
        />
      </Stack>
    </Stack>
  );
};
